import { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';

// Interfaces
export interface LocationCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  createdAt: any;
}

export interface Location {
  id: string;
  name: string;
  description: string;
  lat: number;
  lng: number;
  categoryId: string;
  address?: string;
  photos?: string[];
  dateVisited?: string;
  rating?: number;
  notes?: string;
  createdAt: any;
  isVisible: boolean;
}

export const useLocationsData = () => {
  const [categories, setCategories] = useState<LocationCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Escuchar cambios en categorías
  useEffect(() => {
  const q = query(collection(db, 'locationCategories'), orderBy('order', 'asc'));
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const cats: LocationCategory[] = [];
    querySnapshot.forEach((doc) => {
      cats.push({ id: doc.id, ...doc.data() } as LocationCategory);
    });
    setCategories(cats);
    console.log('✅ Categorías cargadas:', cats.length);
  }, (err) => {
    console.error('❌ Error cargando categorías:', err);
    setError('Error cargando categorías');
  });

  return () => unsubscribe();
}, []); // ← IMPORTANTE: Array vacío

  // Escuchar cambios en ubicaciones
  useEffect(() => {
  const q = query(collection(db, 'locations'), orderBy('createdAt', 'desc'));
  
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const locs: Location[] = [];
    querySnapshot.forEach((doc) => {
      locs.push({ id: doc.id, ...doc.data() } as Location);
    });
    setLocations(locs);
    setLoading(false);
    console.log('✅ Ubicaciones cargadas:', locs.length);
  }, (err) => {
    console.error('❌ Error cargando ubicaciones:', err);
    setError('Error cargando ubicaciones');
    setLoading(false);
  });

  return () => unsubscribe();
}, []); // ← IMPORTANTE: Array vacío

  // CRUD para Categorías
  const addCategory = async (categoryData: Omit<LocationCategory, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'locationCategories'), {
        ...categoryData,
        createdAt: serverTimestamp()
      });
      console.log('✅ Categoría agregada');
    } catch (err) {
      console.error('❌ Error agregando categoría:', err);
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: Partial<LocationCategory>) => {
    try {
      await updateDoc(doc(db, 'locationCategories', id), updates);
      console.log('✅ Categoría actualizada');
    } catch (err) {
      console.error('❌ Error actualizando categoría:', err);
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      // Primero verificar si hay ubicaciones en esta categoría
      const locationsInCategory = locations.filter(loc => loc.categoryId === id);
      if (locationsInCategory.length > 0) {
        throw new Error(`No se puede eliminar: hay ${locationsInCategory.length} ubicaciones en esta categoría`);
      }

      await deleteDoc(doc(db, 'locationCategories', id));
      console.log('✅ Categoría eliminada');
    } catch (err) {
      console.error('❌ Error eliminando categoría:', err);
      throw err;
    }
  };

  // CRUD para Ubicaciones
  const addLocation = async (locationData: Omit<Location, 'id' | 'createdAt'>) => {
    try {
      await addDoc(collection(db, 'locations'), {
        ...locationData,
        createdAt: serverTimestamp()
      });
      console.log('✅ Ubicación agregada');
    } catch (err) {
      console.error('❌ Error agregando ubicación:', err);
      throw err;
    }
  };

  const updateLocation = async (id: string, updates: Partial<Location>) => {
    try {
      await updateDoc(doc(db, 'locations', id), updates);
      console.log('✅ Ubicación actualizada');
    } catch (err) {
      console.error('❌ Error actualizando ubicación:', err);
      throw err;
    }
  };

  const deleteLocation = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'locations', id));
      console.log('✅ Ubicación eliminada');
    } catch (err) {
      console.error('❌ Error eliminando ubicación:', err);
      throw err;
    }
  };

  // Funciones de utilidad
  const getLocationsByCategory = (categoryId: string): Location[] => {
    return locations.filter(loc => loc.categoryId === categoryId && loc.isVisible);
  };

  const getCategoryById = (id: string): LocationCategory | undefined => {
    return categories.find(cat => cat.id === id);
  };

  const getLocationById = (id: string): Location | undefined => {
    return locations.find(loc => loc.id === id);
  };

  const getVisibleLocations = (): Location[] => {
    return locations.filter(loc => loc.isVisible);
  };

  // Funciones para estadísticas
  const getStats = () => {
    const totalCategories = categories.length;
    const totalLocations = locations.filter(loc => loc.isVisible).length;
    const categoriesWithLocations = categories.filter(cat => 
      locations.some(loc => loc.categoryId === cat.id && loc.isVisible)
    ).length;

    return {
      totalCategories,
      totalLocations,
      categoriesWithLocations,
      averageLocationsPerCategory: categoriesWithLocations > 0 ? 
        Math.round(totalLocations / categoriesWithLocations * 10) / 10 : 0
    };
  };

  return {
    // Data
    categories,
    locations,
    loading,
    error,

    // Category CRUD
    addCategory,
    updateCategory,
    deleteCategory,

    // Location CRUD
    addLocation,
    updateLocation,
    deleteLocation,

    // Utility functions
    getLocationsByCategory,
    getCategoryById,
    getLocationById,
    getVisibleLocations,
    getStats
  };
};